import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Settings, FileText, Calendar } from "lucide-react";

export function ApplicationFlowSection() {
  const preparationSteps = [
    {
      title: "GビズIDの取得",
      description: "取得に2週間程度",
      url: "https://www.mhlw.go.jp/seisakunitsuite/bunya/koyou_roudou/roudoukijun/hoken/denshi-shinsei/gbiz/",
      icon: Settings
    },
    {
      title: "Sky Gridとの契約締結",
      description: "研修プログラムの契約手続き",
      icon: FileText
    }
  ];

  const documentSteps = [
    {
      title: "ヒアリングシート記入",
      description: "会社情報、研修参加者リスト、労働保険番号など"
    },
    {
      title: "受講場所の見取り図",
      description: "研修実施場所の詳細図面"
    },
    {
      title: "雇用契約書の写し",
      description: "受講者全員分の雇用契約書"
    }
  ];

  const scheduleSteps = [
    {
      title: "申請開始",
      description: "必要書類が揃い次第、申請を開始します",
      timeline: "即日",
      duration: "書類準備完了後すぐ",
      phase: "申請"
    },
    {
      title: "認可通知",
      description: "労働局より認可通知が発行されます",
      timeline: "約2ヶ月",
      duration: "申請から約60日",
      phase: "審査"
    },
    {
      title: "研修開始",
      description: "認可後、研修プログラムを開始します",
      timeline: "1〜2ヶ月",
      duration: "認可後1〜2ヶ月で開始",
      phase: "実施"
    },
    {
      title: "研修完了",
      description: "このタイミングで経費計上可能になります",
      timeline: "全3回完了",
      duration: "開始から約2ヶ月で完了",
      phase: "完了"
    },
    {
      title: "助成金支給申請",
      description: "実績報告書をSky Gridから貴社に送付、各従業員様の署名必須",
      timeline: "研修完了後",
      duration: "完了後1ヶ月以内に申請",
      phase: "支給申請"
    },
    {
      title: "助成金支給完了",
      description: "助成金の支給が完了します",
      timeline: "4〜6ヶ月後",
      duration: "支給申請から4〜6ヶ月",
      phase: "支給"
    }
  ];

  const requiredDocuments = [
    {
      title: "研修代の支払いを確認する振込通知書",
      detail: "振込依頼人の口座情報または振込先の口座情報が分かるもの"
    },
    {
      title: "研修期間中の賃金台帳または給与明細",
      detail: "研修受講者全員分"
    },
    {
      title: "就業規則",
      detail: "会社の就業規則"
    },
    {
      title: "研修期間中の出勤簿またはタイムカード",
      detail: "研修受講者全員分"
    },
    {
      title: "助成金の受取を希望される通帳口座の写し",
      detail: "金融機関名・支店名・口座番号・口座名義等が分かるもの（通帳の表紙、見開き1ページ目など）"
    }
  ];

  const getPhaseColor = (phase: string) => {
    switch (phase) {
      case "申請": return "bg-gray-800";
      case "審査": return "bg-gray-700";
      case "実施": return "bg-gray-600";
      case "完了": return "bg-gray-500";
      case "支給申請": return "bg-gray-400";
      case "支給": return "bg-black";
      default: return "bg-gray-500";
    }
  };

  return (
    <section className="py-20 px-4 bg-gray-50">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="mb-4 text-3xl md:text-4xl text-gray-900">
            リスキリング助成金申請フロー
          </h2>
          <div className="w-24 h-1 bg-black mx-auto mb-8"></div>
          <p className="text-lg text-gray-600 max-w-4xl mx-auto">
            申請から支給完了まで、詳細なフロー説明
          </p>
        </div>
        
        {/* 事前準備 */}
        <div className="mb-16">
          <h3 className="text-2xl mb-8 text-gray-900">事前準備</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {preparationSteps.map((step, index) => (
              <Card key={index} className="border-gray-200 shadow-sm bg-white">
                <CardHeader>
                  <CardTitle className="flex items-center text-lg text-gray-900">
                    <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center mr-3">
                      <step.icon className="w-4 h-4 text-black" />
                    </div>
                    {step.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 mb-3">{step.description}</p>
                  {step.url && (
                    <a href={step.url} className="text-gray-700 hover:text-black hover:underline text-sm break-all">
                      {step.url}
                    </a>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
        
        {/* 必要書類 */}
        <div className="mb-16">
          <h3 className="text-2xl mb-8 text-gray-900">必要書類のご準備</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {documentSteps.map((step, index) => (
              <Card key={index} className="border-gray-200 shadow-sm bg-white">
                <CardContent className="p-6">
                  <div className="flex items-center mb-3">
                    <div className="w-6 h-6 bg-black text-white rounded-full flex items-center justify-center text-xs mr-3">
                      {index + 1}
                    </div>
                    <h4 className="text-lg text-gray-900">{step.title}</h4>
                  </div>
                  <p className="text-gray-700 text-sm">{step.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
        
        {/* スケジュール - モノトーンタイムライン */}
        <div className="mb-16">
          <h3 className="text-2xl mb-8 text-gray-900">申請から支給完了までのスケジュール</h3>
          <div className="bg-white p-8 rounded-xl border border-gray-200">
            <div className="mb-8 text-center">
              <div className="inline-flex items-center bg-gray-100 rounded-full px-6 py-2">
                <Calendar className="w-4 h-4 mr-2 text-gray-600" />
                <span className="text-sm text-gray-700">全体期間：約9〜12ヶ月</span>
              </div>
            </div>
            
            <div className="relative">
              {/* タイムライン線 */}
              <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gray-200"></div>
              
              <div className="space-y-6">
                {scheduleSteps.map((step, index) => (
                  <div key={index} className="relative flex items-start">
                    {/* フェーズアイコン */}
                    <div className={`relative z-10 w-16 h-16 ${getPhaseColor(step.phase)} rounded-full flex items-center justify-center text-white shadow-lg`}>
                      <span className="text-xs text-center leading-tight">{step.phase}</span>
                    </div>
                    
                    {/* 接続線 */}
                    {index < scheduleSteps.length - 1 && (
                      <div className="absolute left-8 top-16 w-0.5 h-6 bg-gray-200"></div>
                    )}
                    
                    {/* コンテンツカード */}
                    <div className="ml-6 flex-1">
                      <Card className="border-l-4 border-l-gray-300 shadow-sm">
                        <CardContent className="p-6">
                          <div className="flex justify-between items-start mb-3">
                            <h4 className="text-xl text-gray-900">{step.title}</h4>
                            <div className="text-right">
                              <Badge variant="outline" className="border-gray-400 text-gray-700 mb-1">
                                {step.timeline}
                              </Badge>
                              <div className="text-xs text-gray-500">{step.duration}</div>
                            </div>
                          </div>
                          <p className="text-gray-600">{step.description}</p>
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* 期間の詳細説明 - モノトーン */}
            <div className="mt-12 bg-gray-50 p-6 rounded-lg">
              <h4 className="text-lg mb-4 text-gray-900">期間の目安</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div className="text-center">
                  <div className="text-2xl mb-1 text-gray-800">2ヶ月</div>
                  <div className="text-gray-600">申請〜認可</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl mb-1 text-gray-700">2ヶ月</div>
                  <div className="text-gray-600">研修実施期間</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl mb-1 text-black">4〜6ヶ月</div>
                  <div className="text-gray-600">支給申請〜支給</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* 支給申請書類 */}
        <div>
          <h3 className="text-xl sm:text-2xl mb-6 sm:mb-8 text-gray-900">助成金支給申請に必要な書類</h3>
          <Card className="border-gray-200 shadow-sm bg-white">
            <CardHeader>
              <CardTitle className="flex items-center text-lg sm:text-xl text-gray-900">
                <FileText className="w-5 h-5 sm:w-6 sm:h-6 mr-2 sm:mr-3 text-black" />
                必要書類一覧
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 sm:space-y-4">
                {requiredDocuments.map((doc, index) => (
                  <div key={index} className="flex items-start p-3 sm:p-4 bg-gray-50 rounded-lg">
                    <div className="w-6 h-6 sm:w-8 sm:h-8 bg-black text-white rounded-full flex items-center justify-center text-xs sm:text-sm mr-3 sm:mr-4 flex-shrink-0">
                      {index + 1}
                    </div>
                    <div>
                      <h5 className="text-sm sm:text-base text-gray-900 mb-1 leading-tight">{doc.title}</h5>
                      <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">{doc.detail}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
}
